import React, { useState, useEffect } from 'react';
import { Inbox, X, Wifi, RefreshCw, Trash2, AlertCircle, CheckCircle2, Loader2, Copy, Download, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useOfflineQueue } from '../../hooks/useOfflineQueue';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { removeQueuedDraft } from '../../utils/offlineStore';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * sendDraftToServer
 * Replays a queued offline draft snapshot via the appropriate backend endpoint.
 * Returns true on success, false otherwise (caller decides retry vs. mark-failed).
 */
const sendDraftToServer = async (item) => {
  const endpoint = item.target_endpoint === 'update-draft'
    ? `${API}/history/update-draft`
    : `${API}/history/save-draft`;
  // Strip queue-only fields from the payload
  const { id, saved_at, status, last_error, target_endpoint, patient_label, ...payload } = item;
  void id; void saved_at; void status; void last_error; void target_endpoint; void patient_label;
  try {
    const res = await axios.post(endpoint, payload, { withCredentials: true });
    return res?.data?.success === true;
  } catch (e) {
    console.warn('[OfflineQueuePanel] send failed:', e?.message);
    return false;
  }
};

/**
 * OfflineQueuePanel
 * ----------------
 * Slide-out side panel showing all queued offline drafts. Provider can see
 * what's waiting to sync, manually drain the queue when back online, or
 * discard individual drafts.
 *
 * Trigger button (also exported) lives in the header and only appears when
 * the queue is non-empty.
 */
const OfflineQueuePanel = ({ open, onClose }) => {
  const { queue, queueCount, refresh, drainQueue } = useOfflineQueue();
  const { status: connectionStatus } = useConnectionStatus();
  const [draining, setDraining] = useState(false);
  const isOffline = connectionStatus === 'offline';

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleDrain = async () => {
    if (isOffline) {
      toast.error('Still offline — can\'t sync yet');
      return;
    }
    setDraining(true);
    try {
      const { drained, failed } = await drainQueue(sendDraftToServer);
      if (drained > 0) {
        toast.success(`Synced ${drained} draft${drained !== 1 ? 's' : ''} successfully`, { duration: 4000 });
      }
      if (failed > 0) {
        toast.error(`${failed} draft${failed !== 1 ? 's' : ''} failed to sync — kept in queue for retry`, { duration: 5000 });
      }
      if (drained === 0 && failed === 0) {
        toast.info('Nothing to sync');
      }
    } finally {
      setDraining(false);
    }
  };

  const handleDiscard = async (id) => {
    await removeQueuedDraft(id);
    toast.success('Draft discarded');
  };

  // Mar 2, 2026 — Emergency copy/export path for providers whose backend
  // is unreachable (prod platform config issue). Lets them paste queued
  // generated notes directly into their EMR without waiting for sync.
  // Reason: user was 25 notes behind, being harassed by leadership, and
  // couldn't wait for a redeploy — needed to ship these notes to the EMR
  // NOW using only the locally-cached drafts.
  const formatDraftText = (d) => {
    const lines = [];
    if (d.note_title) lines.push(`=== ${d.note_title} ===`);
    if (d.patient_label) lines.push(`Patient: ${d.patient_label}`);
    if (d.chief_complaint) lines.push(`CC: ${d.chief_complaint}`);
    if (d.saved_at) lines.push(`Saved: ${new Date(d.saved_at).toLocaleString()}`);
    lines.push('');
    if (d.clinical_note) {
      lines.push('--- CLINICAL NOTE ---');
      lines.push(d.clinical_note);
      lines.push('');
    }
    if (d.admin_letter) {
      lines.push('--- ADMIN LETTER ---');
      lines.push(d.admin_letter);
      lines.push('');
    }
    if (Array.isArray(d.icd10_suggestions) && d.icd10_suggestions.length) {
      lines.push('--- ICD-10 ---');
      lines.push(d.icd10_suggestions.map((c) => `${c.code || c} — ${c.description || ''}`).join('\n'));
      lines.push('');
    }
    if (Array.isArray(d.referrals) && d.referrals.length) {
      lines.push('--- REFERRALS ---');
      lines.push(d.referrals.map((r) => (typeof r === 'string' ? r : (r.text || r.specialty || JSON.stringify(r)))).join('\n'));
      lines.push('');
    }
    if (Array.isArray(d.imaging_orders) && d.imaging_orders.length) {
      lines.push('--- IMAGING ---');
      lines.push(d.imaging_orders.map((i) => (typeof i === 'string' ? i : (i.text || i.study || JSON.stringify(i)))).join('\n'));
      lines.push('');
    }
    if (d.original_notes) {
      lines.push('--- ORIGINAL DICTATION ---');
      lines.push(d.original_notes);
    }
    return lines.join('\n');
  };

  const handleCopyOne = async (d) => {
    try {
      await navigator.clipboard.writeText(formatDraftText(d));
      toast.success('Draft copied — paste into your EMR now');
    } catch (e) {
      toast.error('Copy failed: ' + (e?.message || 'clipboard blocked'));
    }
  };

  const handleCopyAll = async () => {
    if (queue.length === 0) return;
    try {
      const bundle = queue.map((d, i) => `\n\n${'='.repeat(60)}\nDRAFT ${i + 1} OF ${queue.length}\n${'='.repeat(60)}\n\n${formatDraftText(d)}`).join('\n');
      await navigator.clipboard.writeText(bundle.trim());
      toast.success(`All ${queue.length} drafts copied to clipboard`, { duration: 5000 });
    } catch (e) {
      toast.error('Copy failed: ' + (e?.message || 'clipboard blocked'));
    }
  };

  const handleDownloadAll = () => {
    if (queue.length === 0) return;
    try {
      const bundle = queue.map((d, i) => `\n\n${'='.repeat(60)}\nDRAFT ${i + 1} OF ${queue.length}\n${'='.repeat(60)}\n\n${formatDraftText(d)}`).join('\n');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const blob = new Blob([bundle.trim()], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `writeright-offline-drafts-${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${queue.length} drafts as .txt`);
    } catch (e) {
      toast.error('Download failed: ' + (e?.message || String(e)));
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]"
        onClick={onClose}
        data-testid="offline-queue-backdrop"
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-card border-l border-border z-[10001] shadow-2xl flex flex-col"
        data-testid="offline-queue-panel"
        role="dialog"
        aria-label="Offline Queue"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-amber-400" />
            <h2 className="font-bold text-foreground">
              Offline Queue
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-300 border border-amber-500/30">
                {queueCount}
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            data-testid="offline-queue-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-4 py-2 border-b border-border bg-card/50">
          <Button
            onClick={handleDrain}
            disabled={isOffline || draining || queueCount === 0}
            variant="outline"
            className="w-full gap-2 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40"
            data-testid="offline-queue-drain-btn"
          >
            {draining ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Syncing...
              </>
            ) : isOffline ? (
              <>
                <AlertCircle className="w-4 h-4" />
                Still offline — can&apos;t sync yet
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Sync {queueCount} draft{queueCount !== 1 ? 's' : ''} now
              </>
            )}
          </Button>
          {!isOffline && queueCount > 0 && (
            <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1">
              <Wifi className="w-3 h-3 text-emerald-400" />
              Online — drafts will sync to your history
            </p>
          )}
          {/* Emergency export toolbar — Mar 2, 2026: for providers whose
              backend is unreachable (platform-config issue). Copy or
              download all queued drafts as plain text so they can paste
              straight into the EMR without waiting for a redeploy. */}
          {queueCount > 0 && (
            <div className="mt-2 pt-2 border-t border-border/60 flex gap-1.5" data-testid="emergency-export-toolbar">
              <Button
                onClick={handleCopyAll}
                size="sm"
                variant="outline"
                className="flex-1 h-8 text-xs gap-1.5 border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                data-testid="offline-queue-copy-all"
                title="Copy every queued draft to your clipboard as one bundle"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy All
              </Button>
              <Button
                onClick={handleDownloadAll}
                size="sm"
                variant="outline"
                className="flex-1 h-8 text-xs gap-1.5 border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
                data-testid="offline-queue-download-all"
                title="Download every queued draft as a .txt file"
              >
                <Download className="w-3.5 h-3.5" />
                Download .txt
              </Button>
            </div>
          )}
          {queueCount > 0 && (
            <p className="text-[10px] text-muted-foreground/80 mt-1.5 leading-snug flex items-start gap-1">
              <FileText className="w-3 h-3 mt-0.5 shrink-0" />
              Emergency: sync failing? Copy or download all drafts here and paste into your EMR while the backend gets fixed.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {queue.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
              <CheckCircle2 className="w-10 h-10 text-emerald-500/40 mb-3" />
              <p className="text-sm font-semibold text-foreground">All clear</p>
              <p className="text-xs mt-1">
                No drafts queued. When you save offline, snapshots will appear here.
              </p>
            </div>
          ) : (
            queue.map((draft) => (
              <div
                key={draft.id}
                className="rounded-lg border border-border bg-card/50 px-3 py-2.5 hover:border-amber-500/40 transition-colors"
                data-testid={`offline-queue-item-${draft.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {draft.patient_label || draft.chief_complaint || 'Untitled draft'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(draft.saved_at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                      {draft.notes && (
                        <span className="ml-1.5">· {draft.notes.length} chars</span>
                      )}
                    </p>
                    {draft.status === 'failed' && (
                      <p className="text-[11px] text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Failed last sync — will retry
                      </p>
                    )}
                    {draft.status === 'syncing' && (
                      <p className="text-[11px] text-blue-400 mt-1 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Syncing...
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopyOne(draft)}
                      className="p-1.5 rounded text-muted-foreground hover:text-blue-300 hover:bg-blue-500/10 transition-colors"
                      data-testid={`offline-queue-copy-${draft.id}`}
                      title="Copy this draft to clipboard (paste into EMR)"
                      aria-label="Copy draft"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDiscard(draft.id)}
                      className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      data-testid={`offline-queue-discard-${draft.id}`}
                      title="Discard draft"
                      aria-label="Discard draft"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {draft.notes && (
                  <p className="text-xs text-muted-foreground/80 mt-1.5 line-clamp-2 italic">
                    &ldquo;{draft.notes.slice(0, 140)}{draft.notes.length > 140 ? '…' : ''}&rdquo;
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        <footer className="px-4 py-2.5 border-t border-border bg-card text-xs text-muted-foreground">
          Drafts saved while offline are stored locally on this device only.
          They sync to your history when your connection returns.
        </footer>
      </aside>
    </>
  );
};

export default OfflineQueuePanel;

/**
 * OfflineQueueTriggerButton
 * Header button that opens the Offline Queue panel.
 * Mar 2, 2026 — Previously hidden when queueCount === 0, which meant users
 * couldn't find the offline cache to verify nothing was lost. Now always
 * visible: bright amber pill when drafts are queued, dimmed with "empty"
 * label when clean. This gives a stable, always-known access point.
 */
export const OfflineQueueTriggerButton = ({ onClick }) => {
  const { queueCount } = useOfflineQueue();
  const isEmpty = queueCount === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-semibold transition-colors ${
        isEmpty
          ? 'bg-secondary/40 hover:bg-secondary/60 border-border text-muted-foreground hover:text-foreground'
          : 'bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/40 text-amber-300'
      }`}
      data-testid="offline-queue-trigger"
      title={isEmpty
        ? 'Offline queue is empty — click to view sync history'
        : `${queueCount} offline draft${queueCount !== 1 ? 's' : ''} waiting to sync`}
    >
      <Inbox className="w-3.5 h-3.5" />
      <span>{isEmpty ? '0' : queueCount}</span>
      <span className="hidden sm:inline">{isEmpty ? 'queue' : 'queued'}</span>
    </button>
  );
};
